import { animated, useReducedMotion, useSpring } from "@react-spring/web";
import React, { useRef } from "react";

import { useMeasuredHeight } from "./use-measured-height";
import { clamp } from "./use-scroll-progress";

interface CollapsibleProps {
  /** Collapse progress from 0 (fully expanded) to 1 (fully collapsed). */
  progress: number;
  children: React.ReactNode;
  /** Margin (px) to collapse along with the content. */
  marginTop?: number;
  /** Margin (px) to collapse along with the content. */
  marginBottom?: number;
  /** Multiplier controlling how quickly opacity fades relative to height.
   *  Higher values fade content out earlier, preventing visual squish near
   *  the end of the collapse. Defaults to 1.5. */
  opacityFalloff?: number;
}

/**
 * Animates `height`, `opacity`, and optional vertical margins based on a
 * scroll-linked progress value. Content stays mounted; only the visual
 * dimensions interpolate.
 *
 * Typical usage: pair with `useScrollProgress` and wrap any block that
 * should collapse as the user scrolls.
 */
const Collapsible = ({
  progress,
  children,
  marginTop = 0,
  marginBottom = 0,
  opacityFalloff = 1.5,
}: CollapsibleProps) => {
  const [innerRef, contentHeight] = useMeasuredHeight<HTMLDivElement>();
  const reducedMotion = useReducedMotion();

  // Skip the animation until the first real measurement arrives. Without
  // this, the spring would animate from 0 to the measured height on mount,
  // which reads as an unwanted entrance animation.
  const hasMeasuredRef = useRef(false);
  const isFirstMeasure = contentHeight > 0 && !hasMeasuredRef.current;
  if (contentHeight > 0) {
    hasMeasuredRef.current = true;
  }

  const scale = 1 - progress;
  const opacity = clamp(1 - progress * opacityFalloff, 0, 1);

  const styles = useSpring<{
    height: number;
    opacity: number;
    marginTop: number;
    marginBottom: number;
  }>({
    height: contentHeight * scale,
    marginTop: marginTop * scale,
    marginBottom: marginBottom * scale,
    opacity,
    // Snap immediately when reduced motion is preferred, or on the very
    // first measurement to avoid an entrance animation.
    immediate: !!reducedMotion || isFirstMeasure,
    config: { tension: 280, friction: 32 },
  });

  return (
    <animated.div style={{ ...styles, overflow: "hidden" }}>
      <div ref={innerRef}>{children}</div>
    </animated.div>
  );
};

export default Collapsible;
